import { z } from 'zod'

import { subtotalEither } from '~api/services/payments/payments'
import { images } from '~assets/images'
import { schemas } from '~interface/zod-schema'
import { formatCSVDate } from '~utils/dateUtils'
import { renderCost } from '~utils/numberUtils'

export function poliCardFee(outAmt: number): number {
  const fee = Math.floor((35 * outAmt) / 1000) + 50
  const feeRemainder = (35 * outAmt) % 1000
  return fee + (feeRemainder >= 500 ? 1 : 0)
}

export function poliBankFee(outAmt: number): number {
  const fee = Math.floor((28 * outAmt) / 1000) + 157
  const feeRemainder = (28 * outAmt) % 1000
  return Math.min(995, fee + (feeRemainder >= 500 ? 1 : 0))
}

export const paymentHeaders = [
  'Polimorphic Unique Payment ID',
  'Completed?',
  'Payment Type',
  'Description',
  'Bill Name',
  'Payment Method',
  'Payment Time (UTC Time Zone)',
  'Payer Name',
  'Payer Email',
  'Payment Total',
  'Account Receivable (AR) Code',
]

export function paymentTypeToString(paymentType: PaymentMethodEnumType): string {
  switch (paymentType) {
    case PaymentMethodEnum.enum.BankPayment:
      return 'ACH / E-Check'
    case PaymentMethodEnum.enum.CardPayment:
      return 'Credit'
    case PaymentMethodEnum.enum.CashPayment:
      return 'Cash'
    case PaymentMethodEnum.enum.CheckPayment:
      return 'Check'
    case PaymentMethodEnum.enum.MoneyOrderPayment:
      return 'Money Order'
    case PaymentMethodEnum.enum.OtherPayment:
      return 'Other'
  }
}

export function lookupEntry2ToRecord(lookupEntry: LookupEntry2): string[] {
  return [lookupEntry.key, ...bill2ToRecord(lookupEntry.payment)]
}

export function bill2ToRecord(bill: Bill2): string[] {
  return [
    bill.id,
    bill.complete ? 'Yes' : 'No',
    bill.complete ? 'Digital' : 'Manual',
    bill.description,
    bill.name,
    '',
    bill.completion_time ? formatCSVDate(new Date(bill.completion_time)) : '',
    bill.payer?.name || '',
    bill.payer?.email || '',
    renderCost(subtotalEither(bill)),
    '',
  ]
}

export enum ExportType {
  Polimorphic = 'Polimorphic',
  Southern = 'Southern',
  TylerIncode = 'TylerIncode',
  Springbrook = 'Springbrook',
  Caselle = 'Caselle',
  TylerMunis = 'TylerMunis',
}

export const exportTypeToImages = {
  [ExportType.Polimorphic]: images.logo,
  [ExportType.Southern]: images.southern,
  [ExportType.TylerIncode]: images.tylerTech,
  [ExportType.Springbrook]: images.springbrook,
  [ExportType.Caselle]: images.caselle,
  [ExportType.TylerMunis]: images.tylerTech,
}

export const exportTypeToName = {
  [ExportType.Polimorphic]: 'Polimorphic',
  [ExportType.Southern]: 'Southern',
  [ExportType.TylerIncode]: 'Tyler Incode',
  [ExportType.Springbrook]: 'Springbrook',
  [ExportType.Caselle]: 'Caselle',
  [ExportType.TylerMunis]: 'Tyler Munis',
}

export type Bill2 = z.infer<typeof schemas.Bill2>
export type BillFilter = z.infer<typeof schemas.BillFilter>
export type Charge = z.infer<typeof schemas.Charge>
export const FormatEnum = schemas.FormatEnum
export type FormatEnumType = z.infer<typeof FormatEnum>
export type LookupEntry2 = z.infer<typeof schemas.LookupEntry2>
export type Lookup0 = z.infer<typeof schemas.Lookup0>
export type Lookup2 = z.infer<typeof schemas.Lookup2>
export type Payment0 = z.infer<typeof schemas.Payment0>
export type Payment1 = z.infer<typeof schemas.Payment1>
export const LookupKeyTypeEnum = schemas.LookupKeyTypeEnum
export type PaymentExportRule = z.infer<typeof schemas.PaymentExportRule>
export const PaymentStatusEnum = schemas.StatusE08Enum
export type PaymentStatusEnumType = z.infer<typeof schemas.StatusE08Enum>
export type PaymentMethodEnumType = z.infer<typeof schemas.MethodF1dEnum>
export const PaymentMethodEnum = schemas.MethodF1dEnum
export type Payout2 = z.infer<typeof schemas.Payout2>
export type StripeSource = z.infer<typeof schemas.StripeSource>

export type ConnectStripeViewRequestData = z.infer<typeof schemas.ConnectStripeViewRequestData>
export type ConnectStripeViewResponseData = z.infer<typeof schemas.ConnectStripeViewResponseData>
export type RevenueTypeBreakdown = z.infer<typeof schemas.RevenueTypeBreakdown>
export type RevenueStat = z.infer<typeof schemas.RevenueStat>
export type PaymentInterval = z.infer<typeof schemas.PaymentInterval>
export const StripeSourceTypeEnum = schemas.StripeSourceTypeEnum
